import { View } from 'react-native';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import AnimatedLottieView, { type AnimationObject } from 'lottie-react-native';
import type { UseQueryResult } from '@tanstack/react-query';
import { WhiteLabel } from 'app/types/white-labels';
import loadingIndicatorRefrainAnim from 'app/design/anims/loadingIndicatorRefrain.json';
import loadingIndicatorMutuoAnim from 'app/design/anims/loadingIndicatorMutuo.json';
import loadingIndicatorHauteGaspesieAnim from 'app/design/anims/loadingIndicatorHauteGaspesie.json';
import loadingIndicatorAnim from 'app/design/anims/loadingIndicator.json';
import { isWhiteLabel } from 'app/utils/helpers';

export interface TopLoadingIndicatorState {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export function useTopLoadingIndicator(
  query?: UseQueryResult | UseQueryResult[],
  state?: TopLoadingIndicatorState,
): TopLoadingIndicatorState {
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  // Query results (and inline arrays of them) can change identity on every render.
  // Derive local loading directly; only synchronize the boolean with a parent.
  const queryIsLoading = query
    ? (Array.isArray(query) ? query : [query]).some((item) => item.isFetching && item.data === undefined)
    : undefined;
  const externalIsLoading = state?.isLoading;
  const setExternalIsLoading = state?.setIsLoading;

  useEffect(() => {
    if (queryIsLoading === undefined || externalIsLoading === queryIsLoading) return;
    setExternalIsLoading?.(queryIsLoading);
  }, [queryIsLoading, externalIsLoading, setExternalIsLoading]);

  return {
    isLoading: state?.isLoading ?? queryIsLoading ?? internalIsLoading,
    setIsLoading: setExternalIsLoading ?? setInternalIsLoading,
  };
}

export function TopLoadingIndicator({
  isLoading = true,
  position = 'absolute',
}: {
  isLoading?: boolean;
  position?: 'absolute' | 'relative';
}) {
  const animationRef = useRef<AnimatedLottieView>(null);
  const [animationState, setAnimationState] = useState({ isLoading, fadeOutDone: !isLoading });
  const currentAnimationState =
    animationState.isLoading === isLoading
      ? animationState
      : { isLoading, fadeOutDone: isLoading ? false : animationState.fadeOutDone };
  if (currentAnimationState !== animationState) {
    setAnimationState(currentAnimationState);
  }
  const fadeOutDone = currentAnimationState.fadeOutDone;
  const wasLoopingRef = useRef(false);

  useEffect(() => {
    if (!animationRef.current) return;
    if (isLoading) {
      // Fade-in
      wasLoopingRef.current = false;
      animationRef.current.play(0, 60);
    } else {
      // Fade-out
      // If the animation was already looping, just fade-out (i.e. start at frame 60)
      // If not (because loading was too fast), then play the gradient animation at least once before fading-out (i.e. start at frame 30)
      animationRef.current.play(wasLoopingRef.current ? 60 : 30, 90);
    }
  }, [isLoading]);

  const animationFinished = (isCancelled?: boolean) => {
    // Changing playback segments cancels the previous one; it must not restart
    // the loop or complete the new segment's fade-out.
    if (isCancelled) return;
    if (isLoading) {
      // Loading animation loop
      wasLoopingRef.current = true;
      animationRef.current?.play(30, 60);
    } else {
      // Collapse once the fade-out animation completed (the bar is fully transparent)
      setAnimationState((current) => ({ ...current, fadeOutDone: true }));
    }
  };

  const animSource = isWhiteLabel(WhiteLabel.Refrain)
    ? loadingIndicatorRefrainAnim
    : isWhiteLabel(WhiteLabel.Mutuo)
      ? loadingIndicatorMutuoAnim
      : isWhiteLabel(WhiteLabel.HauteGaspesie)
        ? loadingIndicatorHauteGaspesieAnim
        : loadingIndicatorAnim;

  return (
    <View
      style={{
        height: isLoading || !fadeOutDone ? 4 : 0,
        overflow: 'hidden',
        width: '100%',
        position,
      }}
    >
      <AnimatedLottieView
        loop={false}
        onAnimationFinish={animationFinished}
        ref={animationRef}
        resizeMode="cover"
        source={animSource as AnimationObject}
        style={{
          flexGrow: 1,
          width: '100%',
        }}
      />
    </View>
  );
}
